import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DaySequence, DaySequenceStatus, Job } from '@core/generated/gql-types';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import TimesheetsToAction from './TimesheetsToAction';
import { actionLinkClassName } from '@core/theme/actionTheme.util';

interface TimesheetsToActionSectionProps {
  job: Job;
}

const TimesheetsToActionSection = ({ job }: TimesheetsToActionSectionProps) => {
  const jobId = job?.id;
  const byId = useDaySequenceStore((state) => state.byId);
  const jobDaySequenceIds = useDaySequenceStore((state) =>
    jobId ? state.idsByJobId[jobId] : undefined,
  );
  const fetchAllDaySequencesByJobId = useDaySequenceStore(
    (state) => state.fetchAllDaySequencesByJobId,
  );
  const [isFetching, setIsFetching] = useState(false);

  // Whatever the jobs list cached can be stale or partial, so always pull
  // every page for this job. Cached rows stay on screen while it loads.
  const fetchDaySequences = useCallback(async () => {
    if (!jobId) return;

    setIsFetching(true);
    try {
      await fetchAllDaySequencesByJobId(jobId);
    } catch (error) {
      console.error('Error fetching day sequences for job:', error);
    } finally {
      setIsFetching(false);
    }
  }, [jobId, fetchAllDaySequencesByJobId]);

  useEffect(() => {
    void fetchDaySequences();
  }, [fetchDaySequences]);

  const actionRequiredDaySequences = useMemo<DaySequence[]>(
    () =>
      (jobDaySequenceIds ?? [])
        .map((id) => byId[id])
        .filter(
          (daySequence): daySequence is DaySequence =>
            daySequence?.status === DaySequenceStatus.EscalatedToTenant ||
            daySequence?.status === DaySequenceStatus.RequiresApproval,
        ),
    [jobDaySequenceIds, byId],
  );

  const isLoading = isFetching && actionRequiredDaySequences.length === 0;

  // Only show timesheets section if the job has a user assigned.
  if (!job.userId) {
    return <></>;
  }

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">
          Timesheets to action
        </h3>
        <Link
          to={`/jobs/${job.id}?tab=timesheets`}
          className={`flex cursor-pointer items-center text-sm font-medium ${actionLinkClassName}`}
        >
          View all
        </Link>
      </div>

      <TimesheetsToAction
        job={job}
        actionRequiredDaySequences={actionRequiredDaySequences}
        fetchDaySequences={fetchDaySequences}
        loading={isLoading}
      />
    </>
  );
};

export default TimesheetsToActionSection;
